/**
 * The panel a held corner badge opens.
 *
 * A 词典 entry and a 例句 page each hang a save badge in the card's corner.
 * Once the reader holds the subject, pressing the badge opens this panel under
 * it. The panel says when they saved it and how 复习 is getting on with it, and
 * links to 复习. Taking the subject off the list is the panel's last button.
 *
 * The handler draws the panel's contents (see
 * `src/user/study-standing-markup.ts`) and this module only places them. A
 * badge whose panel cannot be drawn goes back to being a toggle, which is what
 * it was before the panel existed.
 *
 * **It opens by itself on a wide screen.** See `openWhereRoom` in
 * `study-standing-badge.ts`.
 *
 * Only the badge gets a panel. The control beside each row of `/user/ci/` and
 * the segmenter's controls stay toggles, those pages being lists the reader is
 * already managing.
 */

/** Which list a badge saves to. */
export type StandingKind = "ci" | "liju";

/** Where the handler draws each kind's panel, and the field it reads. */
const routes: Readonly<
  Record<StandingKind, { readonly path: string; readonly field: string }>
> = {
  ci: { path: "/user/ci/standing/", field: "word" },
  liju: { path: "/user/liju/standing/", field: "uuid" },
};

/**
 * The class the badge's block wears while its panel is open.
 *
 * `modules/save-word.scss` draws the block and the panel as one box under it,
 * with the badge's button left in the corner to close it again.
 */
const openClass = "cb-standing-open";

/** The class `study-standing-markup.ts` gives the panel's remove button. */
const removeSelector = ".cb-standing-remove";

/** How the panels on one page are told apart, for `aria-controls`. */
let panelsMade = 0;

/** What one panel needs. */
export interface StandingPanelProperties {
  /** The badge's block, which the panel is put inside. */
  readonly control: HTMLElement;

  /** The badge's button, which opens and closes the panel. */
  readonly button: HTMLButtonElement;

  /** Which list the subject is on. */
  readonly kind: StandingKind;

  /** What asks the handler, which a test hands its own. */
  readonly fetched: typeof fetch;

  /** Takes the subject off the reader's list, as a press of the badge did. */
  readonly remove: () => void;
}

/** One badge's panel. */
export class StandingPanel {
  private readonly properties: StandingPanelProperties;
  private readonly panel: HTMLElement;

  constructor(properties: StandingPanelProperties) {
    this.properties = properties;

    const document = properties.control.ownerDocument;

    panelsMade += 1;
    this.panel = document.createElement("div");
    this.panel.className = "cb-standing";
    this.panel.id = `cb-standing-${String(panelsMade)}`;
    this.panel.hidden = true;
    properties.control.append(this.panel);
    properties.button.setAttribute("aria-controls", this.panel.id);

    this.panel.addEventListener("click", (event) => {
      if ((event.target as Element).closest(removeSelector) !== null) {
        this.close();
        this.properties.remove();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen()) {
        this.close();
        this.properties.button.focus();
      }
    });

    document.addEventListener("click", (event) => {
      if (
        this.isOpen() &&
        !this.properties.control.contains(event.target as Node)
      ) {
        this.close();
      }
    });
  }

  /** The panel's own element, for a line put under what the handler drew. */
  get element(): HTMLElement {
    return this.panel;
  }

  /** Whether the panel is showing. */
  isOpen(): boolean {
    return this.panel.hidden === false;
  }

  /**
   * Ask the handler for the panel and show it.
   * @returns Whether there was a panel to show. The handler answers nothing for
   *   a subject the reader does not hold, or for a session that has lapsed.
   */
  async open(): Promise<boolean> {
    const route = routes[this.properties.kind];
    // Called bare, because a browser refuses `fetch` called as a method of
    // anything but the window.
    const { fetched } = this.properties;
    const query = new URLSearchParams({
      [route.field]: this.properties.button.value,
    });

    this.properties.button.setAttribute("aria-busy", "true");

    try {
      const answer = await fetched(`${route.path}?${query.toString()}`);
      const standing = ((await answer.json()) as { standing?: unknown })
        .standing;

      if (typeof standing !== "string") {
        return false;
      }

      this.panel.innerHTML = standing;
      this.panel.hidden = false;
      this.properties.control.classList.add(openClass);
      this.properties.button.setAttribute("aria-expanded", "true");

      return true;
    } finally {
      this.properties.button.removeAttribute("aria-busy");
    }
  }

  /** Hide the panel. */
  close(): void {
    this.panel.hidden = true;
    this.properties.control.classList.remove(openClass);
    this.properties.button.setAttribute("aria-expanded", "false");
  }

  /** Close it if it is open, and otherwise open it. */
  async toggle(): Promise<boolean> {
    if (this.isOpen()) {
      this.close();

      return true;
    }

    return this.open();
  }
}
